import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiFetch } from "../../shared/api";

function normalizePagedResponse(data) {
  if (Array.isArray(data)) {
    return { items: data, count: data.length };
  }
  const items = Array.isArray(data?.results) ? data.results : [];
  const count = typeof data?.count === "number" ? data.count : items.length;
  return { items, count };
}

export const fetchCategories = createAsyncThunk(
  "categories/fetchCategories",
  async ({ search = "", page = 1, pageSize = 25 } = {}) => {
    const qp = new URLSearchParams();
    if (String(search || "").trim()) qp.set("search", String(search || "").trim());
    qp.set("ordering", "name");
    qp.set("page", String(page || 1));
    qp.set("page_size", String(pageSize || 25));
    const data = await apiFetch(`/api/product/categories/?${qp.toString()}`, { method: "GET" });
    return normalizePagedResponse(data);
  }
);

export const fetchCategoryDetail = createAsyncThunk("categories/fetchCategoryDetail", async ({ categoryId }) => {
  return await apiFetch(`/api/product/categories/${categoryId}/`, { method: "GET" });
});

export const createCategory = createAsyncThunk("categories/createCategory", async ({ body }, { rejectWithValue }) => {
  try {
    const created = await apiFetch("/api/product/categories/", { method: "POST", body });
    return created;
  } catch (err) {
    return rejectWithValue(err?.data || { detail: err?.message || "Failed to create category" });
  }
});

export const updateCategory = createAsyncThunk(
  "categories/updateCategory",
  async ({ categoryId, patch }, { rejectWithValue }) => {
    try {
      const updated = await apiFetch(`/api/product/categories/${categoryId}/`, { method: "PATCH", body: patch });
      return updated;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to update category" });
    }
  }
);

export const deleteCategory = createAsyncThunk("categories/deleteCategory", async ({ categoryId }, { rejectWithValue }) => {
  try {
    await apiFetch(`/api/product/categories/${categoryId}/`, { method: "DELETE" });
    return { categoryId };
  } catch (err) {
    return rejectWithValue(err?.data || { detail: err?.message || "Failed to delete category" });
  }
});

const slice = createSlice({
  name: "categories",
  initialState: {
    list: {
      items: [],
      count: 0,
      status: "idle",
      error: null,
      lastSearch: "",
      lastPage: 1,
      lastPageSize: 25,
    },
    byId: {},
    statusById: {},
    errorById: {},
    createStatus: "idle",
    createError: null,
    updateStatusById: {},
    updateErrorById: {},
    deleteStatusById: {},
    deleteErrorById: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state, action) => {
        state.list.status = "loading";
        state.list.error = null;
        state.list.lastSearch = action.meta.arg?.search || "";
        state.list.lastPage = action.meta.arg?.page || 1;
        state.list.lastPageSize = action.meta.arg?.pageSize || 25;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.list.status = "succeeded";
        state.list.items = action.payload?.items || [];
        state.list.count = action.payload?.count ?? state.list.items.length;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.list.status = "failed";
        state.list.error = action.error?.message || "Failed to load categories";
      })
      .addCase(fetchCategoryDetail.pending, (state, action) => {
        const id = String(action.meta.arg.categoryId);
        state.statusById[id] = "loading";
        state.errorById[id] = null;
      })
      .addCase(fetchCategoryDetail.fulfilled, (state, action) => {
        const id = String(action.payload?.id ?? action.meta.arg.categoryId);
        state.statusById[id] = "succeeded";
        state.byId[id] = action.payload;
      })
      .addCase(fetchCategoryDetail.rejected, (state, action) => {
        const id = String(action.meta.arg.categoryId);
        state.statusById[id] = "failed";
        state.errorById[id] = action.error?.message || "Failed to load category";
      })
      .addCase(createCategory.pending, (state) => {
        state.createStatus = "saving";
        state.createError = null;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.createStatus = "succeeded";
        const created = action.payload;
        if (created?.id != null) {
          const id = String(created.id);
          state.byId[id] = created;
          state.list.items = [created, ...(state.list.items || [])];
          state.list.count = Number(state.list.count || 0) + 1;
        }
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.createStatus = "failed";
        const payload = action.payload;
        state.createError =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to create category";
      })
      .addCase(updateCategory.pending, (state, action) => {
        const id = String(action.meta.arg.categoryId);
        state.updateStatusById[id] = "saving";
        state.updateErrorById[id] = null;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        const updated = action.payload;
        const id = String(updated?.id ?? action.meta.arg.categoryId);
        state.updateStatusById[id] = "succeeded";
        if (updated) state.byId[id] = updated;
        state.list.items = (state.list.items || []).map((x) => (x.id === updated?.id ? updated : x));
      })
      .addCase(updateCategory.rejected, (state, action) => {
        const id = String(action.meta.arg.categoryId);
        state.updateStatusById[id] = "failed";
        const payload = action.payload;
        state.updateErrorById[id] =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to update category";
      })
      .addCase(deleteCategory.pending, (state, action) => {
        const id = String(action.meta.arg.categoryId);
        state.deleteStatusById[id] = "saving";
        state.deleteErrorById[id] = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        const id = String(action.payload?.categoryId ?? action.meta.arg.categoryId);
        state.deleteStatusById[id] = "succeeded";
        delete state.byId[id];
        state.list.items = (state.list.items || []).filter((x) => String(x.id) !== id);
        state.list.count = Math.max(0, Number(state.list.count || 0) - 1);
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        const id = String(action.meta.arg.categoryId);
        state.deleteStatusById[id] = "failed";
        const payload = action.payload;
        state.deleteErrorById[id] =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to delete category";
      });
  },
});

export default slice.reducer;

export const selectCategories = (state) => state.categories.list.items || [];
export const selectCategoriesCount = (state) => Number(state.categories.list.count || 0);
export const selectCategoriesStatus = (state) => state.categories.list.status || "idle";
export const selectCategoriesError = (state) => state.categories.list.error || null;

export const selectCategoryById = (id) => (state) => state.categories.byId[String(id)] || null;
export const selectCategoryStatusById = (id) => (state) => state.categories.statusById[String(id)] || "idle";
export const selectCategoryErrorById = (id) => (state) => state.categories.errorById[String(id)] || null;

export const selectCreateCategoryStatus = (state) => state.categories.createStatus || "idle";
export const selectCreateCategoryError = (state) => state.categories.createError || null;

export const selectUpdateCategoryStatusById = (id) => (state) => state.categories.updateStatusById[String(id)] || "idle";
export const selectUpdateCategoryErrorById = (id) => (state) => state.categories.updateErrorById[String(id)] || null;

export const selectDeleteCategoryStatusById = (id) => (state) => state.categories.deleteStatusById[String(id)] || "idle";
export const selectDeleteCategoryErrorById = (id) => (state) => state.categories.deleteErrorById[String(id)] || null;

